import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { DragDropContext } from "@hello-pangea/dnd";
import OrdersColumn from "./OrdersColumn";
import Pusher from "pusher-js";
import { adminApiRequest } from "../utils/adminApi";
import PageLoader from "./PageLoader";

const ORDER_STATUSES = [
  { id: "todo", label: "order received" },
  { id: "inprogress", label: "processing" },
  { id: "completed", label: "completed" },
];

const STATUS_MAP = {
  "to do": "todo",
  todo: "todo",
  "in progress": "inprogress",
  processing: "inprogress",
  completed: "completed",
};

const DB_STATUS_MAP = {
  todo: "to do",
  inprogress: "in progress",
  completed: "completed",
};

const toUkDbString = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${map.year}-${map.month}-${map.day} ${map.hour}:${map.minute}:${map.second}`;
};

export default function OrdersTab({ role }) {
  const [columns, setColumns] = useState({
    todo: [],
    inprogress: [],
    completed: [],
  });
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragLoading, setDragLoading] = useState(false);
  const [pusherError, setPusherError] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());

  const toggleOrderSelection = useCallback((orderId) => {
    setSelectedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedOrderIds(new Set());
  }, []);

  const pollingInterval = useRef(null);

  const sounds = useRef({
    todo: new Audio("/sounds/notification.mp3"),
    inprogress: new Audio("/sounds/inprogress-order.mp3"),
    completed: new Audio("/sounds/completed-order.mp3"),
  });

  const sortOrders = useCallback((orders, columnId) => {
    if (columnId === "completed") {
      return [...orders].sort(
        (a, b) => new Date(b.completed_at) - new Date(a.completed_at),
      );
    }
    return [...orders].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at),
    );
  }, []);

  const normalizeColumns = useCallback((orders) => {
    const col = { todo: [], inprogress: [], completed: [] };

    (orders || []).forEach((o) => {
      const key =
        STATUS_MAP[
          String(o?.status || "")
            .trim()
            .toLowerCase()
        ];
      if (key) col[key].push(o);
    });

    return col;
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const data = await adminApiRequest({
        script: "get-orders",
        token: localStorage.getItem("admin_token"),
      });

      const newColumns = normalizeColumns(data?.orders || []);
      setColumns(newColumns);
    } catch (err) {
      console.error("Polling fetch failed:", err);
    }
  }, [normalizeColumns]);

  const fetchSingleOrder = useCallback(async (orderId) => {
    const data = await adminApiRequest({
      script: "get-order",
      data: { order_id: orderId },
      token: localStorage.getItem("admin_token"),
    });

    const order = data?.order || data?.orders?.[0];
    if (!order?.id) {
      throw new Error("Invalid order response from get-order");
    }

    return order;
  }, []);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      setLoading(true);
      await fetchOrders();
      setLoading(false);
    };

    init();

    if (!process.env.REACT_APP_PUSHER_KEY) {
      console.error("Missing Pusher environment variables");
      return () => {};
    }

    const pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY, {
      cluster: process.env.REACT_APP_PUSHER_CLUSTER,
      forceTLS: true,
    });

    const channel = pusher.subscribe("orders-channel");

    channel.bind("new-order", async (data) => {
      if (!isMounted || !data?.order_id) return;

      try {
        const fullOrder = await fetchSingleOrder(data.order_id);

        if (!fullOrder?.id) {
          fetchOrders();
          return;
        }

        setColumns((prev) => {
          const exists = Object.values(prev)
            .flat()
            .some((o) => o?.id === fullOrder.id);

          if (exists) return prev;

          const updatedTodo = sortOrders([...prev.todo, fullOrder], "todo");

          sounds.current.todo.play().catch(() => {});

          return { ...prev, todo: updatedTodo };
        });
      } catch (err) {
        fetchOrders();
      }
    });

    pusher.connection.bind("state_change", (states) => {
      if (states.current === "disconnected" || states.current === "failed") {
        setPusherError(true);
        if (!pollingInterval.current) {
          pollingInterval.current = setInterval(fetchOrders, 5000);
        }
      }

      if (states.current === "connected") {
        setPusherError(false);
        if (pollingInterval.current) {
          clearInterval(pollingInterval.current);
          pollingInterval.current = null;
        }
      }
    });

    return () => {
      isMounted = false;
      channel.unbind_all();
      channel.unsubscribe();
      pusher.disconnect();

      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    };
  }, [fetchOrders, fetchSingleOrder, sortOrders]);

  const toggleOrder = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const handleDragEnd = async (result) => {
    if (role === "staff") return;
    const { destination, draggableId, source } = result;
    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const orderId = parseInt(draggableId, 10);

    const destKey = destination.droppableId;
    const newDbStatus = DB_STATUS_MAP[destKey];
    const nowUk = toUkDbString(new Date());

    // If the dragged card is part of a multi-selection (and other movable
    // orders are selected too), move every selected order together instead
    // of just the one that was physically dragged.
    // IDs are normalized to Number here because order.id may come back from
    // the API as a string, while orderId (parsed from draggableId) is always
    // a number — comparing them directly would silently fail.
    const movableSelectedIds = Array.from(selectedOrderIds)
      .map((id) => Number(id))
      .filter((id) => {
        const ownerColumn = Object.keys(columns).find((col) =>
          (columns[col] || []).some((o) => Number(o.id) === id),
        );
        return ownerColumn && ownerColumn !== "completed";
      });

    const isMultiDrag =
      movableSelectedIds.length > 1 && movableSelectedIds.includes(orderId);

    const movingIds = isMultiDrag ? movableSelectedIds : [orderId];

    setDragLoading(true);
    const startTime = Date.now();

    setColumns((prev) => {
      const newColumns = {};
      for (const key of Object.keys(prev)) {
        newColumns[key] = Array.from(prev[key] || []);
      }

      const movedOrders = [];

      for (const key of Object.keys(newColumns)) {
        if (key === destKey) continue;
        const list = newColumns[key];
        const remaining = [];
        for (const o of list) {
          if (movingIds.includes(Number(o.id))) {
            movedOrders.push({
              ...o,
              status: newDbStatus,
              moved_at: nowUk,
              ...(destKey === "completed" ? { completed_at: nowUk } : {}),
            });
          } else {
            remaining.push(o);
          }
        }
        newColumns[key] = remaining;
      }

      if (movedOrders.length === 0) return prev;

      const destList = Array.from(newColumns[destKey] || []).concat(
        movedOrders,
      );
      newColumns[destKey] = sortOrders(destList, destKey);

      return newColumns;
    });

    sounds.current?.[destKey]?.play?.().catch(() => {});

    if (isMultiDrag) clearSelection();

    try {
      if (movingIds.length > 1) {
        await adminApiRequest({
          script: "update-orders-status-bulk",
          data: {
            order_ids: movingIds,
            status: newDbStatus,
          },
          token: localStorage.getItem("admin_token"),
        });
      } else {
        await adminApiRequest({
          script: "update-order-status",
          data: {
            order_id: movingIds[0],
            status: newDbStatus,
          },
          token: localStorage.getItem("admin_token"),
        });
      }
      // Do NOT refetch here — the optimistic update already has the correct
      // completed_at timestamp. Refetching immediately risks getting stale
      // server data before the DB write has committed, which wipes completed_at.
    } catch (err) {
      // Only refetch on error to restore correct server state
      await fetchOrders();
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = 1500 - elapsed;
      setTimeout(() => setDragLoading(false), remaining > 0 ? remaining : 0);
    }
  };

  const totalCompletedPrice = useMemo(() => {
    return (columns.completed || []).reduce(
      (sum, order) => sum + (Number(order?.total_price) || 0),
      0,
    );
  }, [columns.completed]);

  if (loading) {
    return <PageLoader message="Fetching Orders..." />;
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      {dragLoading && (
        <PageLoader 
          fullPage 
          message="Updating Status..." 
        />
      )}

      {pusherError && (
        <div className="mb-5 shrink-0 rounded-2xl border border-red-200 bg-red-500 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm">
          ⚠️ Real-time updates unavailable. Falling back to refresh mode.
        </div>
      )}

      {selectedOrderIds.size > 0 && (
        <div className="mb-5 flex shrink-0 items-center justify-between rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700 shadow-sm">
          <span>
            {selectedOrderIds.size} order
            {selectedOrderIds.size === 1 ? "" : "s"} selected — drag one to
            move them all
          </span>
          <button
            type="button"
            onClick={clearSelection}
            className="rounded-lg bg-white px-3 py-1.5 text-xs font-bold text-blue-700 shadow-sm ring-1 ring-blue-200 transition hover:bg-blue-100"
          >
            Clear selection
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1">
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex h-full min-h-0 gap-4 overflow-x-auto pb-2 xl:overflow-x-hidden">
            {ORDER_STATUSES.map(({ id, label }) => (
              <OrdersColumn
                key={id}
                droppableId={id}
                statusLabel={label}
                orders={columns[id]}
                expandedOrderId={expandedOrderId}
                role={role}
                toggleOrder={toggleOrder}
                refetchOrders={fetchOrders}
                selectedOrderIds={selectedOrderIds}
                onSelectToggle={toggleOrderSelection}
                footer={
                  id === "completed" && (columns.completed || []).length > 0 ? (
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-md font-bold text-emerald-700">
                      Total Completed Orders: £{totalCompletedPrice.toFixed(2)}
                    </div>
                  ) : null
                }
              />
            ))}
          </div>
        </DragDropContext>
      </div>
    </div>
  );
}
